import { renderItemCreator } from "pro-features/components/AutocompleteWebCategories/utils";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { RecentService } from "app/services/recent/recentService";
import { isLinesOfBusinessSegment, parseLinesOfBusinessSegmentId } from "app/services/segments/LinesOfBusinessUtils";
import type { ICustomSegment } from "app/services/segments/segmentsApiService";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";

import { DISABLED_TOOLTIPS } from "./const";
import { EDisableState, ESelectionType } from "./types";
import type { IAsset, IGetDisabledStateItem, IResolveDisabledStateParams } from "./types";

export type IListItem = IAsset & {
    isDisabled?: boolean;
};
type IListItems = IListItem[];

const { Website, Segment } = EAssetType;

export const hasQuery = (query: string) => typeof query === "string" && query !== "";

const filterExcludeItems =
    (excludeItems: FixmeType, excludeItemsArray: FixmeType) =>
    ({ id, type }: IListItem) => {
        // Check exact match (type + id)
        if (excludeItems.has(type + id)) {
            return false;
        }
        // For websites, also check if the domain is used in any LOB segment
        if (type === EAssetType.Website && isDomainUsedInLobSegment(id, excludeItemsArray)) {
            return false;
        }
        return true;
    };

export const getListItems = (
    selectedValue: FixmeType,
    onClearSearch: FixmeType,
    renderItemsClick: FixmeType,
    query: string,
    customSegmentsMeta: FixmeType,
    excludeItems: FixmeType,
    websiteResults: FixmeType[] = [],
): IListItems => {
    if (!hasQuery(query) && selectedValue) {
        onClearSearch();
    }
    const websiteItems = getListItemsWebsites(query, websiteResults, excludeItems);
    const segmentItems = getListItemsSegments(query, customSegmentsMeta, excludeItems);
    const companyItems = getListItemsCompanies(query);
    const items = [...websiteItems, ...segmentItems, ...companyItems];

    const excludeItemsEnriched = excludeItems.map(({ type, id }: FixmeType) => type + id);
    const excludeItemsSet = new Set(excludeItemsEnriched);
    const listItems = items
        .filter(filterExcludeItems(excludeItemsSet, excludeItems))
        .map(renderItemCreator(renderItemsClick));
    return listItems;
};

/**
 * Checks if a domain is used in any of the selected LOB segments
 */
const isDomainUsedInLobSegment = (domain: string, excludeItems: IAsset[]): boolean => {
    return excludeItems.some((excludeItem) => {
        if (excludeItem.type !== EAssetType.Segment || !isLinesOfBusinessSegment(excludeItem.id)) {
            return false;
        }
        const parsed = parseLinesOfBusinessSegmentId(excludeItem.id);
        return parsed?.domain === domain;
    });
};

const getListItemsWebsites = (query: string, websiteResults: FixmeType, excludeItems: FixmeType): IListItems =>
    websiteResults.length ? getAutoCompleteWebsites(websiteResults, excludeItems) : getRecentWebsites(excludeItems);

const getAutoCompleteWebsites = (websiteResults: FixmeType, excludeItems: FixmeType): IListItems => {
    const recentWebsites = websiteResults?.map((item: FixmeType) => {
        const { name, image } = item;
        // Disable if: a regular segment with same domain is selected OR a LOB segment with same domain is selected
        const isDisabled =
            excludeItems.some(
                (excludeItem: FixmeType) => excludeItem.type !== EAssetType.Website && excludeItem.displayText === name,
            ) || isDomainUsedInLobSegment(name, excludeItems);

        return {
            displayText: name,
            image,
            type: Website,
            id: name,
            isDisabled,
        };
    });
    return recentWebsites;
};
const getRecentWebsites = (excludeItems: FixmeType): IListItems => {
    const recents = RecentService.getRecentsWebsites();
    const recentWebsites = recents.map(({ data }) => {
        const { mainItem, resources } = data;
        // Disable if: a regular segment with same domain is selected OR a LOB segment with same domain is selected
        const isDisabled =
            excludeItems.some(
                (excludeItem: FixmeType) =>
                    excludeItem.type !== EAssetType.Website && excludeItem.displayText === mainItem,
            ) || isDomainUsedInLobSegment(mainItem, excludeItems);

        return {
            displayText: mainItem,
            id: mainItem,
            image: resources?.mainItemFavIcon,
            type: Website,
            isDisabled,
        };
    });
    // @ts-ignore FIXME: strict migration - Type '{ image: string | undefined; ... }[]' is not assignable to 'IListItems' — IStateResourceObject.mainItemFavIcon is optional but IAsset.image is required; website recents always carry a favicon
    return recentWebsites;
};

const getListItemsSegments = (query = "", customSegmentsMeta: FixmeType, excludeItems: FixmeType): IListItems => {
    const { Segments: segments = [], AccountSegments: accountSegments = [] } = customSegmentsMeta;
    const accountSegmentsEnriched = accountSegments.map((segment: FixmeType) => ({
        ...segment,
        isAccount: true,
    }));
    const filterByQuery = (segment: ICustomSegment) => {
        const { segmentName = "", domain = "" } = segment;
        return (
            segmentName.toLowerCase().includes(query.toLowerCase()) ||
            domain.toLowerCase().includes(query.toLowerCase())
        );
    };

    const segmentsListItems = [...segments, ...accountSegmentsEnriched]
        .filter(filterByQuery)
        .map(({ favicon, segmentName, isAccount, id, domain }) => {
            const isDisabled = excludeItems.some(
                (excludeItem: FixmeType) => excludeItem.type !== EAssetType.Segment && excludeItem.id === domain,
            );

            return {
                displayText: domain,
                image: favicon,
                type: Segment,
                id,
                isAccount,
                name: segmentName,
                isDisabled,
            };
        });
    return segmentsListItems;
};

const getListItemsCompanies = (query: string): IListItems => []; // placeholder for future support of companies

export const addItemToRecentSearches = async (item: IListItem) => {
    const pageId = proNavigator.getCurrentPageId();
    const recentItem = {
        type: "website" as const,
        mainItem: item.id,
        comparedItems: [],
        duration: "",
        country: null,
        comparedDuration: "",
        pageId,
    };
    // @ts-ignore FIXME: strict migration - Argument type not assignable to 'IStateObject' — legacy recent item uses country: null while IStateObject types country as number; behavior preserved
    RecentService.addRecent(recentItem);
};

const resolveDisabledState = ({
    selectedItemsView,
    numberOfSelectableEntities,
    numberOfSelectableSegments,
    type,
    isDisabled,
}: IResolveDisabledStateParams): EDisableState => {
    const isDisabledDueToMaxAmount = numberOfSelectableEntities - Object.keys(selectedItemsView).length <= 0;
    const isDisabledDueToMaxSegments =
        numberOfSelectableSegments -
            Object.values(selectedItemsView).filter(({ type }) => type === EAssetType.Segment).length <=
        0;
    if (isDisabledDueToMaxSegments && type === EAssetType.Segment) {
        return EDisableState.DISABLED_DUE_TO_MAX_SEGMENTS_AMOUNT;
    }
    if (isDisabled) {
        return EDisableState.DISABLED_DUE_TO_DOMAIN_AND_SEGMENTS_OVERLAPPING;
    }
    if (isDisabledDueToMaxAmount) {
        return EDisableState.DISABLED_DUE_TO_MAX_ENTITIES_AMOUNT;
    }

    return EDisableState.ENABLED;
};
export const getDisabledState = ({ item, selectedItemsView, selectionTypeProps }: IGetDisabledStateItem) => {
    // @ts-ignore FIXME: strict migration - 'selectionTypeProps' possibly undefined — always provided by caller (defaults to DEFAULT_SELECTION_TYPE_PROPS)
    const { numberOfSelectableEntities, numberOfSelectableSegments, selectionTypes } = selectionTypeProps;

    const disabledState = resolveDisabledState({
        selectedItemsView,
        numberOfSelectableEntities,
        numberOfSelectableSegments,
        type: item.type,
        isDisabled: item.isDisabled,
    });
    const isChecked =
        // @ts-ignore FIXME: strict migration - 'selectionTypes' possibly undefined — ISelectionTypeProps.selectionTypes is optional but always set via DEFAULT_SELECTION_TYPE_PROPS
        selectionTypes[item.type] === ESelectionType.MULTIPLE && selectedItemsView.hasOwnProperty(item.id);

    const isDisabled = disabledState !== EDisableState.ENABLED && !isChecked;
    // @ts-ignore FIXME: strict migration - Element implicitly has 'any' type indexing DISABLED_TOOLTIPS with EDisableState.ENABLED — ENABLED has no tooltip entry (undefined), tolerated by Tooltip downstream
    const disabledTooltip = DISABLED_TOOLTIPS[disabledState];

    return { isDisabled, isChecked, disabledTooltip };
};
